/**
 * Keyboard Controls Manager
 * Handles all keyboard input with proper encapsulation and cleanup
 * Follows best practices: no memory leaks, single responsibility, type safety
 */

import { frontendLogger } from '../../shared/logger';

export interface KeyboardControlsOptions {
  onAction?: (action: KeyboardAction, data?: any) => void;
}

export type KeyboardAction =
  | 'fullscreen'
  | 'difficulty'
  | 'mute'
  | 'pickup'
  | 'equip'
  | 'sprint_start'
  | 'sprint_stop'
  | 'crouch_start'
  | 'crouch_stop'
  | 'slide_start'
  | 'slide_stop'
  | 'toggle_camera'
  | 'toggle_scoreboard';

export interface MovementState {
  forward: number;  // -1 (back), 0 (none), 1 (forward)
  right: number;    // -1 (left), 0 (none), 1 (right)
  jump: boolean;
}

export class KeyboardControls {
  // Private state
  private readonly pressedKeys = new Set<string>();
  private _isSprinting = false;
  private _isCrouching = false;
  private _isSliding = false;
  private readonly onAction?: (action: KeyboardAction, data?: any) => void;

  // Bound event handlers for cleanup
  private readonly boundHandlers = {
    keyDown: this.handleKeyDown.bind(this),
    keyUp: this.handleKeyUp.bind(this)
  };

  constructor(options: KeyboardControlsOptions = {}) {
    this.onAction = options.onAction;
    this.setupEventListeners();
    frontendLogger.info('KeyboardControls initialized');
  }

  // Public getters for read-only state access
  public get isSprinting(): boolean { return this._isSprinting; }
  public get isCrouching(): boolean { return this._isCrouching; }
  public get isSliding(): boolean { return this._isSliding; }

  private setupEventListeners(): void {
    document.addEventListener('keydown', this.boundHandlers.keyDown);
    document.addEventListener('keyup', this.boundHandlers.keyUp);
  }

  private handleKeyDown(e: KeyboardEvent): void {
    // Alt+Enter for fullscreen
    if (e.code === 'Enter' && e.altKey) {
      e.preventDefault();
      this.onAction?.('fullscreen');
      return;
    }

    // F1-F3 for difficulty selection
    if (e.code === 'F1' || e.code === 'F2' || e.code === 'F3') {
      e.preventDefault();
      let difficulty: 'easy' | 'normal' | 'hard' = 'normal';
      if (e.code === 'F1') difficulty = 'easy';
      else if (e.code === 'F2') difficulty = 'normal';
      else if (e.code === 'F3') difficulty = 'hard';

      this.onAction?.('difficulty', { difficulty });
      frontendLogger.info('Difficulty selected', { difficulty });
      return;
    }

    // Add key to pressed set
    this.pressedKeys.add(e.code);

    // Sprint (Shift)
    if (e.code === 'ShiftLeft' && !this._isSprinting) {
      this._isSprinting = true;
      this.onAction?.('sprint_start');
      frontendLogger.debug('Sprint started');
    }

    // Crouch/Slide (Ctrl)
    if (e.code === 'ControlLeft' && !this._isCrouching) {
      this._isCrouching = true;
      this.onAction?.('crouch_start');

      // Slide if sprinting
      if (this._isSprinting && !this._isSliding) {
        this._isSliding = true;
        this.onAction?.('slide_start');
        frontendLogger.debug('Slide started');
      }
    }

    // Mute/Unmute (M)
    if (e.code === 'KeyM') {
      this.onAction?.('mute');
    }
    
    // Toggle camera view (V)
    if (e.code === 'KeyV') {
      this.onAction?.('toggle_camera');
    }
    
    // Toggle scoreboard (Tab)
    if (e.code === 'Tab') {
      e.preventDefault();
      this.onAction?.('toggle_scoreboard');
    }

    // Pickup (E)
    if (e.code === 'KeyE') {
      this.onAction?.('pickup');
    }

    // Equip items (1-5)
    if (e.code >= 'Digit1' && e.code <= 'Digit5') {
      const slotIndex = parseInt(e.code.substring(5)) - 1;
      this.onAction?.('equip', { slotIndex });
      frontendLogger.debug('Equip slot selected', { slotIndex });
    }
  }

  private handleKeyUp(e: KeyboardEvent): void {
    this.pressedKeys.delete(e.code);

    // Sprint stop
    if (e.code === 'ShiftLeft' && this._isSprinting) {
      this._isSprinting = false;
      this._isSliding = false;  // Stop sliding when sprint ends
      this.onAction?.('sprint_stop');
      if (this._isSliding) {
        this.onAction?.('slide_stop');
      }
      frontendLogger.debug('Sprint stopped');
    }

    // Crouch stop
    if (e.code === 'ControlLeft' && this._isCrouching) {
      this._isCrouching = false;
      this.onAction?.('crouch_stop');
    }
  }

  /**
   * Check if a specific key is currently pressed
   */
  public isKeyPressed(code: string): boolean {
    return this.pressedKeys.has(code);
  }

  /**
   * Check if any of the provided keys are pressed
   */
  public isAnyKeyPressed(...codes: string[]): boolean {
    return codes.some(code => this.pressedKeys.has(code));
  }

  /**
   * Get current movement state based on WASD keys
   */
  public getMovementState(): MovementState {
    const forward = (this.isKeyPressed('KeyS') ? 1 : 0) - (this.isKeyPressed('KeyW') ? 1 : 0);
    const right = (this.isKeyPressed('KeyD') ? 1 : 0) - (this.isKeyPressed('KeyA') ? 1 : 0);
    const jump = this.isKeyPressed('Space');

    return { forward, right, jump };
  }

  /**
   * Get movement input for physics
   * @param isSpectator - If true, returns zero movement
   */
  public getMovementInput(isSpectator = false): { forward: number; right: number; jump: number } {
    if (isSpectator) {
      return { forward: 0, right: 0, jump: 0 };
    }

    const state = this.getMovementState();
    return {
      forward: state.forward,
      right: state.right,
      jump: state.jump ? 1 : 0
    };
  }

  /**
   * Check if player is moving
   */
  public isMoving(): boolean {
    return this.isAnyKeyPressed('KeyW', 'KeyA', 'KeyS', 'KeyD');
  }

  /**
   * Reset all key states (useful when losing focus)
   */
  public reset(): void {
    this.pressedKeys.clear();
    this._isSprinting = false;
    this._isCrouching = false;
    this._isSliding = false;
    frontendLogger.debug('Keyboard state reset');
  }

  /**
   * Cleanup method to remove event listeners and prevent memory leaks
   */
  public destroy(): void {
    document.removeEventListener('keydown', this.boundHandlers.keyDown);
    document.removeEventListener('keyup', this.boundHandlers.keyUp);
    this.reset();
    frontendLogger.info('KeyboardControls destroyed');
  }

  /**
   * Get debug info about current keyboard state
   */
  public getDebugInfo(): {
    pressedKeys: string[];
    isSprinting: boolean;
    isCrouching: boolean;
    isSliding: boolean;
    movement: MovementState;
  } {
    return {
      pressedKeys: Array.from(this.pressedKeys),
      isSprinting: this._isSprinting,
      isCrouching: this._isCrouching,
      isSliding: this._isSliding,
      movement: this.getMovementState()
    };
  }
}
